import { withAnalyticsHeaders } from "./api/http";

type FallbackFetchResult<T> = { rateLimited: true } | { res: Response; data: T };

type ErrorLikePayload = {
  status?: string;
  message?: string;
  reason?: string;
  detail?: string | { message?: string; reason?: string };
};

function normalizeErrorPayload<T>(payload: unknown): T {
  if (!payload || typeof payload !== "object") {
    return { status: "error", message: "Request failed." } as T;
  }

  const errorPayload = payload as ErrorLikePayload;
  const nestedDetail = typeof errorPayload.detail === "object" && errorPayload.detail !== null ? errorPayload.detail : null;
  const message =
    errorPayload.message ?? (typeof errorPayload.detail === "string" ? errorPayload.detail : nestedDetail?.message) ?? "Request failed.";
  const reason = errorPayload.reason ?? nestedDetail?.reason;

  return {
    ...(errorPayload as Record<string, unknown>),
    status: "error",
    message,
    ...(reason ? { reason } : {}),
  } as T;
}

/**
 * POST to each URL in `candidates` in order, skipping to the next on 404/405.
 * Returns immediately with `{ rateLimited: true }` on 429.
 * Returns the last `{ res, data }` pair on any other outcome.
 */
export async function fetchWithFallback<T>(candidates: string[], body: object): Promise<FallbackFetchResult<T>> {
  let lastRes: Response | undefined;
  let lastData: T | undefined;

  for (const url of candidates) {
    const res = await fetch(url, {
      method: "POST",
      headers: withAnalyticsHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });

    if (res.status === 429) return { rateLimited: true };

    const payload = (await res.json()) as T;
    const data = res.ok ? payload : normalizeErrorPayload<T>(payload);
    lastRes = res;
    lastData = data;

    if (res.ok) break;
    if (res.status === 404 || res.status === 405) continue;
    break;
  }

  return { res: lastRes!, data: lastData! };
}

/**
 * Builds the ordered list of endpoint paths to try.
 * If a custom primary endpoint is provided and differs from the default,
 * the default is appended as a fallback.
 */
export function buildEndpointCandidates(primary: string | undefined, defaultEndpoint: string): string[] {
  if (!primary) return [defaultEndpoint];
  return primary === defaultEndpoint ? [primary] : [primary, defaultEndpoint];
}
